/**
 * Offline cache — through the Day 1 storage.js abstraction (Keychain-backed
 * Preferences on native, localStorage on web), not a new persistence
 * mechanism. Caches the last successful response for the two screens named
 * in the Phase 5 brief: LeagueHome's summary payload and the rankings Draft
 * Board reads (StrategyEstablishment's player pool). Keyed per league, so
 * switching leagues never shows one league's cached data under another's
 * name.
 *
 * Usage pattern (see LeagueHome.jsx / StrategyEstablishment.jsx):
 *   1. On every successful load, call `saveOfflineCache(kind, leagueId, data)`.
 *   2. When a load fails with what looks like a genuine network error (not
 *      a 4xx/5xx from a reachable server — see isNetworkError below), call
 *      `loadOfflineCache(kind, leagueId)`. If it returns non-null, render
 *      that instead of an error screen, and show the "You're offline"
 *      banner (OfflineBanner.jsx).
 *
 * Connectivity itself is read via @capacitor/network on native (the only
 * reliable signal in a WKWebView — `navigator.onLine` is not dependable on
 * iOS) and `navigator.onLine` on web, exposed here as `isOnline()` /
 * `onConnectivityChange()` so call sites don't import @capacitor/network
 * directly.
 */

import { Network } from '@capacitor/network'
import { storage } from './storage'
import { IS_NATIVE_APP } from './runtime'

const CACHE_PREFIX = 'jai_offline_cache_'

function cacheKey(kind, leagueId) {
  return `${CACHE_PREFIX}${kind}_${leagueId}`
}

/** Persists `data` (must be JSON-serialisable) as the last-known-good
 *  snapshot for this (kind, leagueId) pair. Fire-and-forget — a failed
 *  write here should never block or fail the caller's own render. */
export function saveOfflineCache(kind, leagueId, data) {
  const payload = JSON.stringify({ data, cachedAt: new Date().toISOString() })
  storage.setItem(cacheKey(kind, leagueId), payload).catch(() => {
    // Best-effort. A cache write failing just means the next offline
    // moment has nothing to fall back on — not worth surfacing.
  })
}

/** Returns `{ data, cachedAt }` if a cached snapshot exists for this
 *  (kind, leagueId) pair, or null. Never throws. */
export async function loadOfflineCache(kind, leagueId) {
  try {
    const raw = await storage.getItem(cacheKey(kind, leagueId))
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/**
 * True when a caught error looks like a real network failure (no server
 * ever answered), rather than the server answering with an error status.
 * Axios sets `.response` only when a response was actually received, so a
 * network drop, DNS failure, or timeout all leave it undefined — that's the
 * one shape worth falling back to cache for. A 404/500/plan-gate response
 * means the server IS reachable and answered honestly; showing stale cached
 * data instead of that real answer would be actively misleading.
 */
export function isNetworkError(err) {
  return !!err && err.response === undefined
}

/** Current connectivity, read the right way per platform (see file header). */
export async function isOnline() {
  if (IS_NATIVE_APP) {
    try {
      const status = await Network.getStatus()
      return status.connected
    } catch {
      return true // if the plugin itself fails, don't falsely claim offline
    }
  }
  return typeof navigator === 'undefined' ? true : navigator.onLine
}

/** Subscribes to connectivity changes; returns an unsubscribe function.
 *  `onChange(online: boolean)` fires on every transition. */
export function onConnectivityChange(onChange) {
  if (IS_NATIVE_APP) {
    let handle
    Network.addListener('networkStatusChange', (status) => onChange(status.connected))
      .then((h) => { handle = h })
    return () => { handle?.remove() }
  }
  const goOnline = () => onChange(true)
  const goOffline = () => onChange(false)
  window.addEventListener('online', goOnline)
  window.addEventListener('offline', goOffline)
  return () => {
    window.removeEventListener('online', goOnline)
    window.removeEventListener('offline', goOffline)
  }
}
